import { Injectable } from '@angular/core';
import { ServiceBase } from 'services/servicebase';
import { HttpService } from 'services/http.service';
import { environment } from 'environments/environment';
import { EmployeeFeedbackModel } from 'services/models/employeeFeedback.model';
import {
  EmployeeFeedbackVisibilityMode,
  EmployeeFeedbackSettingsModel,
} from 'services/models/employeeFeedbackSettings.model';
import {
  EMPLOYEE_FEEDBACK_INSIGHT_CATEGORIES,
  EmployeeFeedbackInsightCategoryInfo,
  EmployeeFeedbackInsightModel,
  mapEmployeeFeedbackInsight,
} from 'services/models/employeeFeedbackInsight.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Response } from 'services/models/response';
import { QueryParams } from 'services/models/odata/queryParams';

@Injectable({
  providedIn: 'root'
})
export class EmployeeFeedbackService extends ServiceBase<EmployeeFeedbackModel> {
  constructor(http: HttpService) {
    super('employeefeedback', http, environment.apiUrl);
  }

  createEntity(data: any): EmployeeFeedbackModel {
    return EmployeeFeedbackModel.create(data);
  }

  
  //enviar feedback
  sendFeedback(feedback: EmployeeFeedbackModel): Observable<any> {
    return this.http.post(`${this.endpoint}/send`, feedback);
  }

  checkMessageContent(message: string): Observable<string> {
    const params = new HttpParams().set('message', message);
    return this.http
      .get(`${this.endpoint}/check-message-content`, { observe: 'response', params }, undefined, undefined, true)
      .pipe(
        map((res: any) => {
          const body = res?.body ?? res;

          if (typeof body === 'string') {
            return body;
          }

          if (!body || typeof body !== 'object') {
            return '';
          }

          const possibleKeys = ['message', 'messageContent', 'feedbackMessage', 'content', 'text'];
          for (const key of possibleKeys) {
            if (key in body && typeof body[key] === 'string') {
              return body[key] as string;
            }
          }

          return '';
        })
      );
  }

  //buscar pelo id
  getById(id: string): Observable<EmployeeFeedbackModel> {
    return this.http.
      get(`${this.endpoint}/${id}`).pipe(
        map((res: any) => this.createEntity(res.body ?? res))
    );
  }

  //buscar feedbacks enviados pelo usuário
  getMySents(queryParams?: any): Observable<EmployeeFeedbackModel[]> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http.get(`${this.endpoint}/my/sents`, { params }).pipe(
      map((res: any) => {
        const body = res.body ?? res;
        if (!Array.isArray(body)) return [];
        return body.map(item => this.createEntity(item));
      })
    );
  }

//buscar feedbacks recebidos
  getMyReceived(queryParams?: any): Observable<EmployeeFeedbackModel[]> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http.get(`${this.endpoint}/my/received`, { params }).pipe(
      map((res: any) => {
        const body = res.body ?? res;
        if (!Array.isArray(body)) return [];
        return body.map(item => this.createEntity(item));
      })
    );
  }

  //buscar feedbacks por funcionário


listByEmployee(
    employeeId: string,
    params: QueryParams = null
  ): Observable<Response<EmployeeFeedbackModel[]>> {
    return this.customList<EmployeeFeedbackModel>(
      params,`employee/${employeeId}`, EmployeeFeedbackModel
    );
  }


  //buscar feedbacks por gestor
  getByManager(managerId: string, queryParams?: any): Observable<Response<EmployeeFeedbackModel[]>> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http.get(`${this.endpoint}/manager/${managerId}`, { observe: 'response', params }).pipe(
      map(res => {
        const response = new Response<EmployeeFeedbackModel[]>();
        const body = res.body ?? res;
        response.data = Array.isArray(body) ? body.map((item: any) => this.createEntity(item)) : [];
        response.count = Number(res.headers.get('x-count') || 0);
        response.now = res.headers.get('x-now') ?? "";
        return response;
      })
    );
  }

  //buscar feedbacks enviados pelo gestor
  getManagerSents(queryParams?: any): Observable<Response<EmployeeFeedbackModel[]>> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http.get(`${this.endpoint}/manager/sents`, { observe: 'response', params }).pipe(
      map(res => {
        const response = new Response<EmployeeFeedbackModel[]>();
        const body = res.body ?? res;
        response.data = Array.isArray(body) ? body.map((item: any) => this.createEntity(item)) : [];
        response.count = Number(res.headers.get('x-count') || 0);
        response.now = res.headers.get('x-now') ?? "";
        return response;
      })
    );
  }

  exportByFilters(queryParams?: Record<string, string | number | boolean>): Observable<HttpResponse<ArrayBuffer>> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, String(queryParams[key]));
      });
    }

    return this.http.get(
      `${this.endpoint}/export-by-filters/odata`,
      { responseType: 'arraybuffer', observe: 'response', params },
      this.baseUrl,
      undefined,
      true
    );
  }

  getManagerSummary(queryParams?: any): Observable<any> {
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http.get(`${this.endpoint}/manager/summary`, { params }).pipe(
      map((res: any) => res.body ?? res)
    );
  }

  getAvailableFilters(): Observable<any> {
    return this.http.get(`${this.endpoint}/filters`).pipe(
      map((res: any) => res.body ?? res)
    );
  }

  getMetrics(filters?: any): Observable<any> {
    let params = new HttpParams();

    if (filters) {
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== null && item !== undefined && item !== '') {
              params = params.append(key, item);
            }
          });
          return;
        }

        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    return this.http.get(`${this.endpoint}/metrics`, { params }).pipe(
      map((res: any) => {
        const body = res.body ?? res;
        const insights = Array.isArray(body?.insights)
          ? body.insights
          : Array.isArray(body?.Insights)
            ? body.Insights
            : [];

        return {
          ...body,
          insights: insights.map((item: any) => mapEmployeeFeedbackInsight(item)),
        };
      })
    );
  }

  getVisibilitySettings(): Observable<EmployeeFeedbackSettingsModel> {
    return this.http.get(`${this.endpoint}/settings`).pipe(
      map((res: any) => {
        const body = res.body ?? res ?? {};
        return { mode: this.normalizeVisibilityMode(body?.mode ?? body?.Mode) };
      })
    );
  }

  updateVisibilitySettings(mode: EmployeeFeedbackVisibilityMode): Observable<EmployeeFeedbackVisibilityMode> {
    return this.http.put(`${this.endpoint}/settings`, { mode }).pipe(
      map(() => mode)
    );
  }

  private normalizeVisibilityMode(mode: unknown): EmployeeFeedbackVisibilityMode {
    if (typeof mode === 'number') {
      if (mode === EmployeeFeedbackVisibilityMode.ManagerDecides || mode === EmployeeFeedbackVisibilityMode.AlwaysVisible) {
        return mode;
      }

      return EmployeeFeedbackVisibilityMode.Hidden;
    }

    if (typeof mode === 'string') {
      const normalizedMode = mode.trim().toLowerCase();

      if (normalizedMode === 'managerdecides') {
        return EmployeeFeedbackVisibilityMode.ManagerDecides;
      }

      if (normalizedMode === 'alwaysvisible') {
        return EmployeeFeedbackVisibilityMode.AlwaysVisible;
      }

      if (normalizedMode === 'hidden') {
        return EmployeeFeedbackVisibilityMode.Hidden;
      }

      const numericValue = Number(mode);
      if (numericValue === EmployeeFeedbackVisibilityMode.ManagerDecides || numericValue === EmployeeFeedbackVisibilityMode.AlwaysVisible) {
        return numericValue;
      }
    }

    return EmployeeFeedbackVisibilityMode.Hidden;
  }


}
